import { Controller } from '@hotwired/stimulus'
import { Modal } from 'flowbite'
import Camera from './utils/camera'
import type { ModalOptions, ModalInterface } from 'flowbite'
import { createElement, Minus } from 'lucide'
import { DirectUpload } from '@rails/activestorage'
import Stopwatch from './utils/stopwatch'

interface UploadedFile {
  signed_id: string
  filename: string
}

// Connects to data-controller="checklist-modal"
export default class extends Controller {
  static targets = [
    'modal',
    'video',
    'alert',
    'takePhoto',
    'takeVideo',
    'mediaContainer',
    'stopVideo',
    'form',
    'recordingDuration',
    'progressContainer',
    'submitButton',
    'backButton',
    'openCameraButton',
    'switchCamera',
    'switchCameraContainer',
  ]
  declare readonly modalTarget: HTMLElement
  declare readonly videoTarget: HTMLVideoElement
  declare readonly alertTarget: HTMLElement
  declare readonly takePhotoTarget: HTMLButtonElement
  declare readonly takeVideoTarget: HTMLButtonElement
  declare readonly stopVideoTarget: HTMLButtonElement
  declare readonly mediaContainerTarget: HTMLDivElement
  declare readonly formTarget: HTMLFormElement
  declare readonly recordingDurationTarget: HTMLDivElement
  declare readonly progressContainerTargets: HTMLDivElement[]
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly backButtonTarget: HTMLAnchorElement
  declare readonly openCameraButtonTarget: HTMLButtonElement
  declare readonly switchCameraTarget: HTMLButtonElement
  declare readonly switchCameraContainerTarget: HTMLElement

  declare readonly hasBackButtonTarget: boolean
  declare readonly hasSubmitButtonTarget: boolean
  declare readonly hasOpenCameraButtonTarget: boolean

  private readonly modalOptions: ModalOptions = {
    placement: 'center',
    backdrop: 'dynamic',
    backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-90',
    closable: true,
    onHide: () => {
      this.#stopCamera()
      this.#hideAlert()
    },
    onShow: () => {
      this.#startCamera()
    },
  }
  private modal: ModalInterface | null = null
  private camera: Camera | null = null
  private files: { id: string; blob: Blob }[] = []
  private uploadedFileIds: string[] = []
  private stopwatch: Stopwatch | null = null

  connect() {
    this.modal = new Modal(this.modalTarget, this.modalOptions)
    this.camera = new Camera(this.videoTarget)
    this.stopwatch = new Stopwatch(
      (time) => {
        this.recordingDurationTarget.innerText = time
      },
      () => {
        this.recordingDurationTarget.innerText = '00:00'
      }
    )
  }

  disconnect() {
    this.closeModal()
  }

  openModal() {
    this.modal?.show()
  }

  closeModal() {
    this.modal?.hide()
  }

  async submitForm(e: SubmitEvent) {
    e.preventDefault()
    if (!confirm('Apakah anda yakin?')) {
      return
    }

    const originalLabel = this.hasSubmitButtonTarget ? this.submitButtonTarget.textContent : null

    try {
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.textContent = "Saving..."
      this.#toggleBtn(false)
      if (this.files.length > 0) {
        this.progressContainerTargets.forEach((element) => {
          element.classList.remove('hidden')
        })
        await this.#uploadAllFiles()
      }

      this.uploadedFileIds.forEach((id) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'checklist_submission_item[medias][]'
        input.value = id
        this.formTarget.appendChild(input)
      })

      this.formTarget.submit()
      this.#toggleBtn(true)
      if (this.hasSubmitButtonTarget && originalLabel) this.submitButtonTarget.textContent = originalLabel
    } catch (error) {
      console.error(error)
      this.#toggleBtn(true)
      if (this.hasSubmitButtonTarget && originalLabel) this.submitButtonTarget.textContent = originalLabel
    }
  }

  deleteFile(e: PointerEvent & { params: Record<string, string> }): void {
    e.preventDefault()
    const params = e.params
    if (!params.id) {
      return
    }

    const indexToBeDeleted = this.files.findIndex(
      (file) => file.id === params.id
    )
    this.files.splice(indexToBeDeleted, 1)
    this.#removePreview(params.id)
  }

  async capturePhoto() {
    if (!this.camera) {
      return
    }

    try {
      const blob = await this.camera.capturePhoto()
      this.#addFile(blob)
      this.modal?.hide()
    } catch (error) {
      this.#showAlert((error as Error).message)
      console.error('Failed to capture photo:', error)
    }
  }

  startRecording(): void {
    if (!this.camera) {
      return
    }

    try {
      this.camera.startRecording()
      this.#toggleStopVideoBtn(true)
      this.stopwatch?.start()
    } catch (error) {
      this.#showAlert('Failed to start recording')
      console.error(error)
    }
  }

  async stopRecording() {
    if (!this.camera) {
      return
    }

    try {
      const blob = await this.camera.stopRecording()
      this.stopwatch?.stop()
      this.#addFile(blob)
      this.#toggleStopVideoBtn(false)
      this.modal?.hide()
    } catch (error) {
      console.error(error)
    }
  }

  async getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === 'videoinput')
  }

  async switchCamera(): Promise<void> {
    this.#hideAlert()
    if (!this.camera) {
      return
    }

    try {
      const cameras = await this.getAvailableCameras()
      if (cameras.length <= 1) {
        this.#showAlert('Only one camera available')
        return
      }

      this.#toggleCapturePhotoBtn(false)

      const currentDeviceId = this.camera.getCurrentDeviceId()
      let nextDeviceIndex = 0

      if (currentDeviceId) {
        const currentIndex = cameras.findIndex(
          (camera) => camera.deviceId === currentDeviceId
        )
        nextDeviceIndex = (currentIndex + 1) % cameras.length
      }

      const nextDeviceId = cameras[nextDeviceIndex].deviceId
      await this.camera.switchCamera(nextDeviceId)

      this.#toggleCapturePhotoBtn(true)
      this.#toggleSwitchCameraBtn(cameras.length > 1)
    } catch (error) {
      this.#toggleCapturePhotoBtn(true)
      const cameras = await this.getAvailableCameras()
      this.#toggleSwitchCameraBtn(cameras.length > 1)
      this.#showAlert((error as Error).message)
      console.error('Failed to switch camera:', error)
    }
  }

  async #startCamera() {
    if (!this.camera) {
      return
    }

    try {
      const cameras = await this.getAvailableCameras()
      if (cameras.length === 0) {
        throw new Error('No camera found on this device')
      }

      await this.camera.start(cameras[0].deviceId)
      this.#toggleCapturePhotoBtn(true)
      this.#toggleSwitchCameraBtn(cameras.length > 1)
    } catch (error) {
      this.#showAlert((error as Error).message)
      console.error(error)
    }
  }

  #stopCamera() {
    if (this.camera) {
      this.camera.stop()
      this.#toggleCapturePhotoBtn(false)
    }
  }

  #showAlert(msg: string) {
    this.alertTarget.innerText = msg
    this.alertTarget.classList.remove('hidden')
  }

  #hideAlert() {
    this.alertTarget.innerText = ''
    this.alertTarget.classList.add('hidden')
  }

  #toggleCapturePhotoBtn(status: boolean): void {
    this.takePhotoTarget.disabled = status ? false : true
    this.takeVideoTarget.disabled = status ? false : true
    this.switchCameraTarget.disabled = status ? false : true

    if (status) {
      this.takePhotoTarget.classList.remove('text-neutral-700')
      this.takeVideoTarget.classList.remove('text-neutral-700')
      this.switchCameraTarget.classList.remove('text-neutral-700')
    } else {
      this.takePhotoTarget.classList.add('text-neutral-700')
      this.takeVideoTarget.classList.add('text-neutral-700')
      this.switchCameraTarget.classList.add('text-neutral-700')
    }
  }

  #toggleSwitchCameraBtn(show: boolean): void {
    if (show) {
      this.switchCameraContainerTarget.classList.remove('hidden')
    } else {
      this.switchCameraContainerTarget.classList.add('hidden')
    }
  }

  #blobType(blob: Blob) {
    const type = blob.type

    if (type.startsWith('image/')) {
      return 'image'
    } else if (type.startsWith('video/')) {
      return 'video'
    } else {
      return 'unknown'
    }
  }

  #addFile(blob: Blob): void {
    const id = crypto.randomUUID()
    this.files.push({ id, blob })
    this.#createPreview(id, blob)
  }

  #createPreview(id: string, blob: Blob) {
    const div = document.createElement('div')
    div.id = id
    div.classList.add('relative')

    const btn = document.createElement('button')
    btn.classList.add('absolute', 'right-0', 'mx-4', 'my-2', 'z-10')
    btn.dataset.action = 'click->checklist-modal#deleteFile'
    btn.dataset.checklistModalIdParam = id

    const minusIcon = createElement(Minus, {
      class: 'text-neutral-50',
    })
    btn.appendChild(minusIcon)
    div.appendChild(btn)

    if (this.#blobType(blob) === 'image') {
      const img = document.createElement('img')
      img.classList.add('h-auto', 'w-full', 'rounded-base')
      img.src = URL.createObjectURL(blob)
      div.appendChild(img)
    }

    if (this.#blobType(blob) === 'video') {
      const video = document.createElement('video')
      video.classList.add('h-auto', 'w-full', 'rounded-base')
      video.src = URL.createObjectURL(blob)
      video.controls = true
      div.appendChild(video)
    }

    const progressContainer = document.createElement('div')
    progressContainer.dataset.checklistModalTarget = 'progressContainer'
    progressContainer.classList.add(
      'absolute',
      'top-0',
      'flex',
      'justify-center',
      'items-center',
      'w-full',
      'h-full',
      'bg-neutral-100/70',
      'z-15',
      'hidden'
    )
    const progress = document.createElement('span')
    progress.id = `progress-${id}`
    progress.innerText = '0%'
    progressContainer.appendChild(progress)
    div.appendChild(progressContainer)

    this.mediaContainerTarget.appendChild(div)
  }

  #removePreview(id: string) {
    const el = document.getElementById(id)
    el?.remove()
  }

  #toggleStopVideoBtn(status: boolean): void {
    this.takeVideoTarget.parentElement?.classList.remove(
      status ? 'flex' : 'hidden'
    )
    this.takeVideoTarget.parentElement?.classList.add(
      status ? 'hidden' : 'flex'
    )

    this.stopVideoTarget.parentElement?.classList.remove(
      status ? 'hidden' : 'flex'
    )
    this.stopVideoTarget.parentElement?.classList.add(
      status ? 'flex' : 'hidden'
    )
  }

  #toggleBtn(status: boolean): void {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !status
      this.submitButtonTarget.classList.toggle('opacity-50', !status)
      this.submitButtonTarget.classList.toggle('hover:bg-success-strong', status)
    }

    if (this.hasOpenCameraButtonTarget) {
      this.openCameraButtonTarget.disabled = !status
    }

    if (this.hasBackButtonTarget) {
      this.backButtonTarget.style.pointerEvents = status ? '' : 'none'
    }
  }

  async #uploadAllFiles(): Promise<void> {
    this.uploadedFileIds = []
    const total = this.files.length

    for (let i = 0; i < total; i++) {
      const file = this.files[i]

      const uploadedFile = await this.#uploadSingleFile(file.blob, file.id)
      this.uploadedFileIds.push(uploadedFile.signed_id)
    }
  }

  #uploadSingleFile(blob: Blob, id: string): Promise<UploadedFile> {
    return new Promise((resolve, reject) => {
      const file = new File([blob], id)
      const progressEl = document.getElementById(`progress-${id}`) as HTMLElement

      const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener('progress', (event) => {
            const progress = Math.floor((event.loaded / event.total) * 100)
            progressEl.innerText = `${progress}%`
          })
        },
      })

      upload.create((error, blobData) => {
        if (error) {
          reject(error)
        } else {
          resolve(blobData as UploadedFile)
        }
      })
    })
  }
}
